import { Inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { select, Store } from '@ngrx/store'
import { from, zip } from 'rxjs'
import {
  catchError,
  filter,
  map,
  switchMap,
  withLatestFrom
} from 'rxjs/operators'

import { ErrorReportingService } from '../../shared/services/error-reporting.service'
import { DRR_BASE_HREF } from '../../shared/shared.config'
import { AAAStore } from '../../store/root-reducer'
import {
  notifyServiceAvailabilityFailure,
  serviceAvailabilitySuccess,
} from '../advisories/advisories.actions'
import {
  selectAdvisories,
  selectAdvisoryAcknowledged,
} from '../advisories/advisories.selectors'
import {
  selectDefaultVehicle,
  selectIsVehicleChangeAllowed,
} from '../auth/auth.selectors'
import { selectActiveCallStatus } from '../dashboard/calls-statuses/call-status.selectors'
import { selectNeedsTow } from '../issue/issue.selectors'
import { PROCESS_LOCATION_STEP, processLocationStep, setExcludedAreasFailure, setExcludedAreasSuccess } from '../location/location.actions'
import { selectInExcludedArea } from '../location/location.selectors'
import { RouteTypes } from '../main.routes'
import { assignDefaultVehicle } from '../member/member.actions'
import { StepTypes } from '../ui/ui.types'
import { currentEditStepLocation } from '../wizard/wizard.selectors'
import { WizardService } from '../wizard/wizard.service'
import {
  SET_SERVICING_CLUB_CONFIGS,
  setServicingClubConfigsFailure,
  setServicingClubConfigsRequest,
  setServicingClubConfigsSuccess,
} from './servicing-club.actions'
import {
  createSelectConfigByServicingClub,
  selectIsEnabled,
} from './servicing-club.selectors'
import { ServicingClubService } from './servicing-club.service'
import { ServicesConfiguration } from './servicing-club.types'

@Injectable()
export class ServicingClubEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<AAAStore>,
    private router: Router,
    private errorReportingService: ErrorReportingService,
    private wizardService: WizardService,
    private servicingClubService: ServicingClubService,
    @Inject(DRR_BASE_HREF) private drrBaseHref: string
  ) {}

  handleServicingClubConfigRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof setServicingClubConfigsRequest>>(
        SET_SERVICING_CLUB_CONFIGS.REQUEST
      ),
      withLatestFrom(this.store$.pipe(select(createSelectConfigByServicingClub))),
      switchMap(([action, selectConfigByServicingClub]) => {
        const club = action.payload.club
        const clubConfig = selectConfigByServicingClub.memoized(club)

        return clubConfig
          ? [
            setServicingClubConfigsSuccess({
              payload: {
                club,
                config: clubConfig,
              },
            }),
          ]
          : this.servicingClubService.getServicingClubConfigs$(club).pipe(
            map((serviceConfiguration: ServicesConfiguration) =>
              setServicingClubConfigsSuccess({
                payload: {
                  club,
                  config: serviceConfiguration,
                },
              })
            ),
            catchError((error) =>
              from(
                this.errorReportingService.notifyErrorObservable(
                  error,
                  setServicingClubConfigsFailure
                )
              )
            )
          )
      })
    )
  )

  waitForLocationRequiredActions$ = createEffect(() => {
    const excludedAreas$ = this.actions$.pipe(ofType(setExcludedAreasSuccess, setExcludedAreasFailure));
    // Navigation gates on selectIsEnabled, which is backed by service-availability;
    // wait for that response before processing the location step.
    const serviceAvailability$ = this.actions$.pipe(ofType(serviceAvailabilitySuccess, notifyServiceAvailabilityFailure));
    return zip(excludedAreas$, serviceAvailability$).pipe(
      map(() => processLocationStep())
    );
  })

  handleNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PROCESS_LOCATION_STEP),
        withLatestFrom(
          this.store$.pipe(select(currentEditStepLocation)),
          this.store$.pipe(select(selectNeedsTow)),
          this.store$.pipe(select(selectIsVehicleChangeAllowed)),
          this.store$.pipe(select(selectDefaultVehicle)),
          this.store$.pipe(select(selectActiveCallStatus)),
          this.store$.pipe(select(selectIsEnabled)),
          this.store$.pipe(select(selectInExcludedArea)),
          this.store$.pipe(select(selectAdvisories)),
          this.store$.pipe(select(selectAdvisoryAcknowledged)),
        ),
        // An enabled club with advisory messages holds navigation until the user
        // acknowledges the informational dialog (Next). The advisory itself stays
        // in the store so the side menu / service-tracking keep showing it.
        filter(
          ([_, _2, _3, _4, _5, activeCall, isEnabled, inExcludedArea, advisories, advisoryAcknowledged]) =>
            !Boolean(activeCall) &&
            isEnabled &&
            !inExcludedArea &&
            (advisories.length === 0 || advisoryAcknowledged)
        ),
        map(
          ([
            _,
            currentStepUrl,
            needsTow,
            isVehicleChangeAllowed,
            defaultVehicle,
            _2,
            _3,
          ]) => {
            if (currentStepUrl && !needsTow) {
              this.wizardService.backToEditUrl(currentStepUrl)
            } else {
              this.router.navigate([this.drrBaseHref, RouteTypes.STEPS], {
                queryParams: { step: isVehicleChangeAllowed ? StepTypes.VEHICLE : StepTypes.ISSUE },
              })

              if (defaultVehicle) {
                this.store$.dispatch(assignDefaultVehicle())
              }
            }
          }
        )
      ),
    { dispatch: false }
  )
}
